import { calcBMRAtDate, calcTDEEFromActive, calcTDEEFromFactor, getNetCalories } from "./physiology";

type EntryLike = {
  date: string;
  weight?: number | null;
  caloriesIn?: number | null;
  totalBurned?: number | null;
  activeCalories?: number | null;
  netCalories?: number | null;
  bmr?: number | null;
  tdeeSource?: string | null;
  [k: string]: any;
};

type VitalsLike = {
  height?: number | null;
  gender?: string | null;
  birthday?: string | null;
  activityFactor?: number | null;
  currentWeightLbs?: number | null;
};

function resolveTdeeForEntry({
  entry,
  bestKnownWeight,
  activityFactor,
  vitals,
}: {
  entry: EntryLike;
  bestKnownWeight: number | null;
  activityFactor: number;
  vitals: VitalsLike;
}) {
  const todayYmd = new Date().toISOString().slice(0, 10);
  const shouldInferFromActivityFactor = entry.date < todayYmd;

  if (Number.isFinite(entry.totalBurned)) {
    return { tdee: Number(entry.totalBurned), source: "loggedTotal" };
  }

  const bmr = Number.isFinite(bestKnownWeight)
    ? calcBMRAtDate({
      weightLbs: Number(bestKnownWeight),
      heightIn: Number(vitals.height),
      gender: String(vitals.gender ?? ""),
      birthday: String(vitals.birthday ?? ""),
      onDateYmd: entry.date,
    })
    : null;

  if (Number.isFinite(entry.activeCalories) && bmr !== null) {
    return { tdee: calcTDEEFromActive({ bmr, activeCalories: Number(entry.activeCalories) }), source: "fromActive" };
  }

  if (bmr !== null && shouldInferFromActivityFactor) {
    return { tdee: calcTDEEFromFactor({ bmr, factor: activityFactor }), source: "fromActivityFactor" };
  }

  return { tdee: null, source: "unknown" };
}

export function reconcileEntriesDerivedMetrics(entriesAsc: EntryLike[], vitals: VitalsLike): EntryLike[] {
  let lastKnownWeight: number | null = Number.isFinite(vitals.currentWeightLbs) ? Number(vitals.currentWeightLbs) : null;
  const factor = Number.isFinite(vitals.activityFactor) ? Number(vitals.activityFactor) : 1.3;

  return entriesAsc.map((entry) => {
    const bestKnownWeight = Number.isFinite(entry.weight) ? Number(entry.weight) : lastKnownWeight;
    if (Number.isFinite(entry.weight)) lastKnownWeight = Number(entry.weight);

    const bmr = Number.isFinite(bestKnownWeight)
      ? calcBMRAtDate({
        weightLbs: Number(bestKnownWeight),
        heightIn: Number(vitals.height),
        gender: String(vitals.gender ?? ""),
        birthday: String(vitals.birthday ?? ""),
        onDateYmd: entry.date,
      })
      : null;

    const resolved = resolveTdeeForEntry({
      entry,
      bestKnownWeight,
      activityFactor: factor,
      vitals,
    });

    const nextNet = entry.caloriesIn !== null && entry.caloriesIn !== undefined && resolved.tdee !== null
      ? getNetCalories(Number(entry.caloriesIn), resolved.tdee)
      : null;

    return {
      ...entry,
      bmr,
      totalBurned: resolved.tdee,
      tdeeSource: entry.tdeeSource === "demo" ? "demo" : resolved.source,
      netCalories: nextNet,
    };
  });
}
